import { Box, IconButton, InputAdornment } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useAdjustmentLotQuantityCalculation, useGetAdjustmentLotProductTest } from "../../hooks"
import { AdjustmentDuplicateLotError } from "../AdjustmentDuplicateLotError"

import { Button } from "@/components/common/Button"
import { FormNumericField, FormSelect, FormTextField } from "@/components/UiKit"
import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { PRODUCT_PACKAGING_TYPE_OPTIONS } from "@/constants/product"
import { type Product } from "@/graphql/codegen/graphql"
import { getUntestedLotMessage } from "@/utils/lot"

interface AddPalletsAdjustmentLotFieldSetProps {
  index: number
  product: Product
  uomName: string
  isLast: boolean
  showDelete: boolean
  isDuplicate: boolean
  onAdd: () => void
  onRemove: () => void
}

export const AddPalletsAdjustmentLotFieldSet: FC<AddPalletsAdjustmentLotFieldSetProps> = ({
  index,
  product,
  uomName,
  isLast,
  showDelete,
  isDuplicate,
  onAdd,
  onRemove,
}) => {
  const { control, watch } = useFormContext()

  const baseFieldPath = `palletRow.lots.${index}`
  const fieldPaths = {
    lotNumber: `${baseFieldPath}.lotNumber` as const,
    batchNumber: `${baseFieldPath}.batchNumber` as const,
    quantityPerPackage: `${baseFieldPath}.quantityPerPackage` as const,
    packagingType: `${baseFieldPath}.packagingType` as const,
    unitCount: `${baseFieldPath}.unitCount` as const,
    quantity: `${baseFieldPath}.quantity` as const,
  }

  const [lotNumber, batchNumber] = watch([fieldPaths.lotNumber, fieldPaths.batchNumber])

  const { handleQuantityPerPackageChange, handleUnitCountChange, handleQuantityChange } =
    useAdjustmentLotQuantityCalculation({
      fieldPaths: {
        quantityPerPackage: fieldPaths.quantityPerPackage,
        unitCount: fieldPaths.unitCount,
        quantity: fieldPaths.quantity,
      },
    })

  const { shouldShowUntestedLotWarning } = useGetAdjustmentLotProductTest({
    productId: product.productId,
    lotNumber,
    batchNumber,
  })

  return (
    <Box className='flex items-start gap-x-4' data-testid={`inventory.add-pallets-adjustment-lot-field-set-${index}`}>
      <Box className='flex flex-col gap-y-2' style={{ flex: "2 2 0" }}>
        <Box className='flex gap-x-4'>
          {isLast ? (
            <Box className='flex flex-1'>
              <FormTextField
                control={control}
                name={fieldPaths.lotNumber}
                label='Lot #'
                required
                className='flex-1'
                slotProps={{ input: { sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 } } }}
                data-testid={`inventory.add-pallets-adjustment-lot-field-set.lot-number-field-${index}`}
              />
              <Button
                color='primary'
                className='mt-[22px] rounded-l-none rounded-r px-1'
                onClick={onAdd}
                data-testid={`inventory.add-pallets-adjustment-lot-field-set.add-lot-button-${index}`}
              >
                <AddOutlined />
              </Button>
            </Box>
          ) : (
            <FormTextField
              control={control}
              name={fieldPaths.lotNumber}
              label='Lot #'
              required
              className='flex-1'
              data-testid={`inventory.add-pallets-adjustment-lot-field-set.lot-number-field-${index}`}
            />
          )}
          <FormTextField
            control={control}
            name={fieldPaths.batchNumber}
            label='Batch #'
            className='flex-1'
            data-testid={`inventory.add-pallets-adjustment-lot-field-set.batch-number-field-${index}`}
          />
        </Box>
        {shouldShowUntestedLotWarning && (
          <WarningMessage
            message={getUntestedLotMessage(batchNumber)}
            data-testid={`inventory.add-pallets-adjustment-lot-field-set.untested-lot-warning-${index}`}
          />
        )}
        {isDuplicate && (
          <AdjustmentDuplicateLotError
            data-testid={`inventory.add-pallets-adjustment-lot-field-set.duplicate-lot-error-${index}`}
          />
        )}
      </Box>
      <FormNumericField
        control={control}
        name={fieldPaths.quantityPerPackage}
        label='Pkg UoM'
        required
        allowNegative={false}
        decimalScale={2}
        className='flex-1'
        slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
        onChange={handleQuantityPerPackageChange}
        data-testid={`inventory.add-pallets-adjustment-lot-field-set.package-quantity-field-${index}`}
      />
      <FormSelect
        control={control}
        name={fieldPaths.packagingType}
        label='Packaging'
        required
        placeholder='Select one'
        options={PRODUCT_PACKAGING_TYPE_OPTIONS}
        className='flex-1'
        data-testid={`inventory.add-pallets-adjustment-lot-field-set.packaging-field-${index}`}
      />
      <FormNumericField
        control={control}
        name={fieldPaths.unitCount}
        label='Units'
        required
        allowNegative={false}
        decimalScale={0}
        className='flex-1'
        onChange={handleUnitCountChange}
        data-testid={`inventory.add-pallets-adjustment-lot-field-set.units-count-field-${index}`}
      />
      <FormNumericField
        control={control}
        name={fieldPaths.quantity}
        label='Total Lot UoM'
        required
        allowNegative={false}
        decimalScale={2}
        className='flex-1'
        slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
        onChange={handleQuantityChange}
        data-testid={`inventory.add-pallets-adjustment-lot-field-set.quantity-field-${index}`}
      />
      <Box className='mt-[22px] w-10'>
        {showDelete && (
          <IconButton
            onClick={onRemove}
            data-testid={`inventory.add-pallets-adjustment-lot-field-set.remove-lot-button-${index}`}
          >
            <DeleteOutlined className='text-gray-600' />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}
